import React, { useState, useMemo } from 'react';
import { Target, Plus, X, CheckCircle, TrendingUp, TrendingDown, Zap, Trophy, Trash2, XCircle, Lightbulb, Sparkles, Crosshair } from 'lucide-react';
import { MINI_BLOCK_TEMPLATES, METRIC_TYPES, calculateBlockProgress, suggestMiniBlocks } from '../lib/miniBlocks';
import { suggestChampionObjectives, evaluateChampionObjective } from '../lib/championObjectives';
import ChampionObjectiveModal from './shell/ChampionObjectiveModal';
import ChampionSelect from './ChampionSelect';
import Modal from '../design-system/Modal';
import Button from '../design-system/Button';

const DIFFICULTY_COLORS = {
  easy: 'text-emerald-400 border-emerald-400/30',
  medium: 'text-amber-400 border-amber-400/30',
  hard: 'text-rose-400 border-rose-400/30'
};

function CreateBlockModal({ onClose, onSave, champions, version, currentGameCount }) {
  const [mode, setMode] = useState('template');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [custom, setCustom] = useState({
    title: '',
    description: '',
    metric_type: 'winrate',
    metric_operator: '>=',
    metric_target: 54,
    target_games: 10,
    filter_champion: null
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const block = mode === 'template' && selectedTemplate
      ? { ...selectedTemplate, started_at_game_count: currentGameCount, status: 'active' }
      : { ...custom, started_at_game_count: currentGameCount, status: 'active' };

    // Clean up fields that shouldn't go to DB
    delete block.id;
    delete block.difficulty;

    if (!block.title) return;
    setSaving(true);
    try {
      await onSave(block);
    } catch (e) {
      alert('Erreur : ' + e.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-2 md:p-4 overflow-y-auto">
      <div className="bg-zinc-950 border border-cyan-400/30 w-full max-w-3xl my-2 md:my-8" style={{ boxShadow: '0 0 40px rgba(34, 211, 238, 0.2)' }}>
        <div className="border-b border-zinc-800 p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-fuchsia-400" />
            <div>
              <div className="text-[10px] tracking-[0.3em] text-cyan-400 uppercase">Nouveau Mini-Bloc</div>
              <div className="font-black text-lg">Objectif de 10 games</div>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setMode('template')} className={`p-3 border font-semibold tracking-wider transition ${mode === 'template' ? 'bg-cyan-400 text-black border-cyan-400' : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}>TEMPLATES</button>
            <button onClick={() => setMode('custom')} className={`p-3 border font-semibold tracking-wider transition ${mode === 'custom' ? 'bg-fuchsia-400 text-black border-fuchsia-400' : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}>CUSTOM</button>
          </div>

          {mode === 'template' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
              {MINI_BLOCK_TEMPLATES.map(t => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTemplate(t)}
                  className={`p-4 border text-left transition ${selectedTemplate?.id === t.id ? 'border-cyan-400 bg-cyan-400/10' : 'border-zinc-800 hover:border-zinc-700 bg-zinc-950'}`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="font-bold text-sm text-white">{t.title}</div>
                    <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 border ${DIFFICULTY_COLORS[t.difficulty]}`}>{t.difficulty}</span>
                  </div>
                  <div className="text-xs text-zinc-400">{t.description}</div>
                  {t.filter_champion && <div className="text-[10px] text-fuchsia-400 mt-1 font-mono">Filtre : {t.filter_champion}</div>}
                </button>
              ))}
            </div>
          )}

          {mode === 'custom' && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-zinc-600 font-mono">Titre</label>
                <input type="text" value={custom.title} onChange={(e) => setCustom({ ...custom, title: e.target.value })} placeholder="Ex: Zéro défaite Caitlyn" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm mt-1 focus:border-cyan-400 focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] text-zinc-600 font-mono">Description</label>
                <input type="text" value={custom.description} onChange={(e) => setCustom({ ...custom, description: e.target.value })} placeholder="Ex: 3 victoires consécutives sur Caitlyn" className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm mt-1 focus:border-cyan-400 focus:outline-none" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-zinc-600 font-mono">Métrique</label>
                  <select value={custom.metric_type} onChange={(e) => setCustom({ ...custom, metric_type: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm mt-1 focus:border-cyan-400 focus:outline-none">
                    {Object.entries(METRIC_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-zinc-600 font-mono">Opérateur</label>
                  <select value={custom.metric_operator} onChange={(e) => setCustom({ ...custom, metric_operator: e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm mt-1 focus:border-cyan-400 focus:outline-none">
                    <option value=">=">≥ (au moins)</option>
                    <option value="<=">≤ (au plus)</option>
                    <option value=">">&gt; (plus que)</option>
                    <option value="<">&lt; (moins que)</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-zinc-600 font-mono">Cible</label>
                  <input type="number" step="0.1" value={custom.metric_target} onChange={(e) => setCustom({ ...custom, metric_target: +e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm mt-1 focus:border-cyan-400 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-[10px] text-zinc-600 font-mono">Nombre de games</label>
                <input type="number" value={custom.target_games} onChange={(e) => setCustom({ ...custom, target_games: +e.target.value })} className="w-full bg-zinc-900 border border-zinc-800 text-white p-2 text-sm mt-1 focus:border-cyan-400 focus:outline-none" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <input type="checkbox" checked={!!custom.filter_champion} onChange={(e) => setCustom({ ...custom, filter_champion: e.target.checked ? 'Caitlyn' : null })} />
                  <label className="text-[10px] text-zinc-400 font-mono tracking-wider uppercase">Filtrer sur un champion</label>
                </div>
                {custom.filter_champion && (
                  <ChampionSelect value={custom.filter_champion} onChange={(v) => setCustom({ ...custom, filter_champion: v })} champions={champions} version={version} accent="fuchsia" />
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-zinc-800">
            <button onClick={onClose} disabled={saving} className="flex-1 border border-zinc-800 text-zinc-400 p-3 hover:border-zinc-700 transition font-semibold tracking-wider text-sm">ANNULER</button>
            <button onClick={handleSave} disabled={saving || (mode === 'template' && !selectedTemplate) || (mode === 'custom' && !custom.title)} className="flex-[2] bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black p-3 hover:opacity-90 transition font-black tracking-wider disabled:opacity-50">{saving ? 'CRÉATION...' : 'LANCER LE MINI-BLOC'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const BlockCard = ({ block, progress, onComplete, onAbandon, onFail, onDelete }) => {
  const isActive = block.status === 'active';
  const isCompleted = block.status === 'completed';
  const isAbandoned = block.status === 'abandoned';
  const isFailed = block.status === 'failed';

  const progressBg = isCompleted
    ? 'from-cyan-400 to-emerald-400'
    : isFailed
      ? 'from-rose-500 to-rose-700'
      : isAbandoned
        ? 'from-zinc-600 to-zinc-700'
        : progress.projectedSuccess ? 'from-cyan-400 to-fuchsia-400' : 'from-rose-400 to-amber-400';
  const borderColor = isCompleted
    ? 'border-cyan-400/50'
    : isFailed
      ? 'border-rose-400/40'
      : isAbandoned
        ? 'border-zinc-800'
        : progress.projectedSuccess ? 'border-cyan-400/30' : 'border-amber-400/30';

  return (
    <div className={`border ${borderColor} bg-zinc-950 p-5 relative overflow-hidden`}>
      {isCompleted && <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-400/10 rounded-full blur-3xl" />}
      {isFailed && <div className="absolute top-0 right-0 w-32 h-32 bg-rose-400/10 rounded-full blur-3xl" />}

      <div className="relative">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className={`font-black text-lg ${isCompleted ? 'text-cyan-400' : isFailed ? 'text-rose-400' : isAbandoned ? 'text-zinc-500 line-through' : 'text-white'}`}>{block.title}</div>
              {isCompleted && <Trophy size={14} className="text-cyan-400" />}
              {isFailed && <XCircle size={14} className="text-rose-400" />}
              {isAbandoned && <XCircle size={14} className="text-zinc-500" />}
            </div>
            {block.description && <div className="text-xs text-zinc-400">{block.description}</div>}
            {block.filter_champion && <div className="text-[10px] text-fuchsia-400 font-mono mt-1">Filtre : {block.filter_champion}</div>}
          </div>
          {isActive && (
            <button onClick={() => onAbandon(block.id)} className="text-zinc-600 hover:text-rose-400 transition" title="Abandonner">
              <X size={14} />
            </button>
          )}
          {!isActive && (
            <button onClick={() => onDelete(block.id)} className="text-zinc-600 hover:text-rose-400 transition" title="Supprimer">
              <Trash2 size={14} />
            </button>
          )}
        </div>

        {/* Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-[10px] font-mono text-zinc-500">
            <span>GAMES JOUÉES</span>
            <span>{progress.gamesPlayed}/{progress.targetGames}</span>
          </div>
          <div className="h-2 bg-zinc-900 overflow-hidden">
            <div className={`h-full bg-gradient-to-r ${progressBg}`} style={{ width: `${progress.progressPercent}%` }} />
          </div>
        </div>

        {/* Metric */}
        <div className="mt-4 pt-4 border-t border-zinc-800 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold">Valeur actuelle</div>
            <div className={`text-2xl font-black ${isCompleted ? 'text-cyan-400' : isFailed ? 'text-rose-400' : progress.projectedSuccess ? 'text-cyan-400' : 'text-rose-400'}`}>
              {progress.currentValue}{METRIC_TYPES[block.metric_type]?.unit}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold">Cible</div>
            <div className="text-2xl font-black text-white">
              {block.metric_operator} {block.metric_target}{METRIC_TYPES[block.metric_type]?.unit}
            </div>
          </div>
        </div>

        {/* Actions */}
        {isActive && progress.isCompleted && (
          <button onClick={() => onComplete(block.id)} className="mt-4 w-full bg-gradient-to-r from-cyan-400 to-emerald-400 text-black p-3 font-black tracking-wider hover:opacity-90 transition flex items-center justify-center gap-2">
            <CheckCircle size={16} /> VALIDER LE BLOC
          </button>
        )}
        {/* V6.3 : bouton Échec quand objectif raté (target games atteint mais pas validé) */}
        {isActive && progress.isFailed && (
          <div className="mt-4 space-y-2">
            <div className="border border-rose-400/30 bg-rose-400/5 p-3 text-xs text-rose-300">
              ❌ Objectif non atteint sur {progress.targetGames} games.
            </div>
            <button onClick={() => onFail(block.id)} className="w-full bg-rose-500 text-white p-3 font-black tracking-wider hover:opacity-90 transition flex items-center justify-center gap-2">
              <XCircle size={16} /> MARQUER COMME ÉCHEC
            </button>
          </div>
        )}
        {isActive && !progress.isCompleted && !progress.isFailed && progress.gamesPlayed > 0 && (
          <div className="mt-4 flex items-center gap-2 text-xs">
            {progress.projectedSuccess ? (
              <><TrendingUp size={14} className="text-cyan-400" /><span className="text-cyan-400 font-mono">En bonne voie</span></>
            ) : (
              <><TrendingDown size={14} className="text-rose-400" /><span className="text-rose-400 font-mono">Hors cible</span></>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// V6.3 — Modal de suggestions automatiques
// ============================================================
function SuggestionsModal({ suggestions, onClose, onAccept, currentGameCount }) {
  const [accepting, setAccepting] = useState(null);

  const handleAccept = async (sug) => {
    setAccepting(sug.id);
    try {
      const block = {
        title: sug.title,
        description: sug.description,
        metric_type: sug.metric_type,
        metric_operator: sug.metric_operator,
        metric_target: sug.metric_target,
        target_games: sug.target_games,
        filter_champion: null,
        started_at_game_count: currentGameCount,
        status: 'active'
      };
      await onAccept(block);
    } finally {
      setAccepting(null);
    }
  };

  const priorityBadge = (priority) => {
    const colors = {
      high: 'bg-rose-500/20 text-rose-400 border-rose-400/40',
      medium: 'bg-amber-500/20 text-amber-400 border-amber-400/40',
      low: 'bg-cyan-500/20 text-cyan-400 border-cyan-400/40'
    };
    const labels = { high: 'PRIORITÉ HAUTE', medium: 'PRIORITÉ MOYENNE', low: 'BONUS' };
    return (
      <span className={`text-[9px] font-mono font-bold tracking-wider border px-1.5 py-0.5 ${colors[priority]}`}>
        {labels[priority]}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-2 md:p-4 overflow-y-auto">
      <div className="bg-zinc-950 border border-cyan-400/30 w-full max-w-2xl my-2 md:my-8" style={{ boxShadow: '0 0 40px rgba(34, 211, 238, 0.2)' }}>
        <div className="border-b border-zinc-800 p-5 flex items-center justify-between sticky top-0 bg-zinc-950 z-10">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-fuchsia-400" />
            <div>
              <div className="text-[10px] tracking-[0.3em] text-cyan-400 uppercase flex items-center gap-1">
                <Sparkles size={10} /> Suggestions Automatiques
              </div>
              <div className="font-black text-lg">Objectifs proposés</div>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5">
          {suggestions.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-cyan-400 text-4xl mb-2">🎯</div>
              <div className="text-zinc-300 font-bold mb-2">Tu es sur la bonne voie</div>
              <div className="text-xs text-zinc-500 font-mono max-w-sm mx-auto">
                Tes stats récentes sont solides — pas de suggestion automatique pour l'instant. Reviens quand tu auras plus de games ou si tes patterns évoluent.
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs text-zinc-400 mb-4 font-mono">
                Basé sur tes <strong className="text-cyan-400">20 dernières games</strong>. Choisis les objectifs qui t'intéressent.
              </div>
              <div className="space-y-3">
                {suggestions.map(sug => (
                  <div key={sug.id} className="border border-zinc-800 bg-zinc-900/50 p-4 hover:border-zinc-700 transition">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <div className="font-bold text-white">{sug.title}</div>
                          {priorityBadge(sug.priority)}
                        </div>
                        <div className="text-xs text-zinc-400 mb-2">{sug.description}</div>
                        <div className="text-[10px] text-zinc-500 font-mono italic">📊 {sug.reason}</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-3 mt-3 border-t border-zinc-800">
                      <div className="text-xs font-mono text-zinc-500">
                        Cible : <span className="text-cyan-400 font-bold">{sug.metric_operator} {sug.metric_target}{METRIC_TYPES[sug.metric_type]?.unit}</span> sur {sug.target_games} games
                      </div>
                      <button
                        onClick={() => handleAccept(sug)}
                        disabled={accepting === sug.id}
                        className="bg-gradient-to-r from-cyan-400 to-emerald-400 text-black px-4 py-2 font-black text-xs tracking-wider hover:opacity-90 transition flex items-center gap-2 disabled:opacity-50"
                      >
                        {accepting === sug.id ? '...' : <><Plus size={12} /> ACCEPTER</>}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-zinc-800 p-4">
          <button
            onClick={onClose}
            className="w-full border border-zinc-800 text-zinc-400 p-3 hover:border-zinc-700 transition font-semibold tracking-wider text-sm"
          >
            FERMER
          </button>
        </div>
      </div>
    </div>
  );
}


export default function ObjectivesTab({ games, miniBlocks, onCreate, onComplete, onAbandon, onFail, onDelete, champions, version, championPool = [] }) {
  const [showCreate, setShowCreate] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [champObjModal, setChampObjModal] = useState(null); // nom du champion ou null

  const active = useMemo(() => miniBlocks.filter(b => b.status === 'active').map(b => ({ block: b, progress: calculateBlockProgress(b, games) })), [miniBlocks, games]);
  const completed = useMemo(() => miniBlocks.filter(b => b.status === 'completed'), [miniBlocks]);
  const abandoned = useMemo(() => miniBlocks.filter(b => b.status === 'abandoned'), [miniBlocks]);
  const failed = useMemo(() => miniBlocks.filter(b => b.status === 'failed'), [miniBlocks]);

  // V7.7.2 : objectifs champion actifs + suggestions
  const championObjectives = useMemo(() =>
    miniBlocks.filter(b => b.type === 'champion_objective' && b.status === 'active'),
    [miniBlocks]
  );

  const championSuggestions = useMemo(() => {
    const mains = Array.isArray(championPool)
      ? championPool.filter(p => p?.status === 'main' || p?.status === 'flex').map(p => p.name)
      : [];
    // Fallback : si pas de pool, on prend les champions les plus joués
    let candidates = mains;
    if (candidates.length === 0 && Array.isArray(games)) {
      const counts = {};
      games.forEach(g => { if (g.champion) counts[g.champion] = (counts[g.champion] || 0) + 1; });
      candidates = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([n]) => n);
    }
    const out = [];
    for (const champ of candidates) {
      // Skip si déjà un objectif actif sur ce champion
      if (championObjectives.some(o => o.data?.champion === champ)) continue;
      const sugg = suggestChampionObjectives(games, champ);
      if (sugg.length > 0) out.push({ champion: champ, suggestions: sugg });
    }
    return out;
  }, [games, championPool, championObjectives]);


  // V6.3 : Suggestions automatiques basées sur patterns récents
  const suggestions = useMemo(() => suggestMiniBlocks(games, miniBlocks), [games, miniBlocks]);
  const hasSuggestions = suggestions.length > 0;

  // V6.3 : Taux de réussite (completed / completed + failed)
  const finishedCount = completed.length + failed.length;
  const successRate = finishedCount > 0 ? Math.round(completed.length / finishedCount * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header + create */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 bg-gradient-to-b from-cyan-400 to-fuchsia-400" />
          <div>
            <div className="text-[10px] tracking-[0.4em] text-zinc-500 uppercase font-semibold"><Zap size={12} className="inline mr-1" /> Mini-Blocs</div>
            <div className="text-xs text-zinc-400 mt-1">Des objectifs mesurables de 10 games pour structurer ton climb</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasSuggestions && (
            <button
              onClick={() => setShowSuggestions(true)}
              className="border border-cyan-400/50 bg-cyan-400/10 text-cyan-400 px-4 py-2 font-black text-sm tracking-wider hover:bg-cyan-400/20 transition flex items-center gap-2 relative"
              title={`${suggestions.length} suggestion${suggestions.length > 1 ? 's' : ''} disponible${suggestions.length > 1 ? 's' : ''}`}
            >
              <Lightbulb size={14} />
              <span className="hidden md:inline">SUGGESTIONS</span>
              <span className="bg-cyan-400 text-black text-[10px] font-mono px-1.5 py-0.5 rounded-full">{suggestions.length}</span>
            </button>
          )}
          <button onClick={() => setShowCreate(true)} className="bg-gradient-to-r from-cyan-400 to-fuchsia-400 text-black px-4 py-2 font-black text-sm tracking-wider hover:opacity-90 transition flex items-center gap-2">
            <Plus size={14} /> <span className="hidden md:inline">NOUVEAU BLOC</span>
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-zinc-800 p-4">
          <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-1">Actifs</div>
          <div className="text-3xl font-black text-cyan-400">{active.length}</div>
        </div>
        <div className="border border-zinc-800 p-4">
          <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-1">Complétés</div>
          <div className="text-3xl font-black text-emerald-400">{completed.length}</div>
        </div>
        <div className="border border-zinc-800 p-4">
          <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-1">Échecs</div>
          <div className="text-3xl font-black text-rose-400">{failed.length}</div>
        </div>
        <div className="border border-zinc-800 p-4">
          <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-1">Abandonnés</div>
          <div className="text-3xl font-black text-zinc-500">{abandoned.length}</div>
        </div>
      </div>

      {/* V6.3 : Taux de réussite */}
      {finishedCount > 0 && (
        <div className="border border-zinc-800 bg-zinc-950 p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase font-semibold mb-1">Taux de réussite</div>
            <div className="text-xs text-zinc-400 font-mono">{completed.length} succès sur {finishedCount} blocs terminés (échecs + complétés)</div>
          </div>
          <div className={`text-4xl font-black ${successRate >= 60 ? 'text-cyan-400' : successRate >= 40 ? 'text-amber-400' : 'text-rose-400'}`}>
            {successRate}%
          </div>
        </div>
      )}

      {/* V7.7.2 : Objectifs par champion */}
      <div>
        <div className="text-[10px] tracking-[0.4em] text-fuchsia-400 uppercase font-semibold mb-3 flex items-center gap-2">
          <Crosshair size={12} /> OBJECTIFS PAR CHAMPION
        </div>

        {/* Objectifs champion actifs */}
        {championObjectives.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            {championObjectives.map(obj => {
              const evalResult = evaluateChampionObjective(obj, games);
              const data = obj.data || {};
              const pct = data.target_value
                ? Math.min(100, Math.round(((evalResult.current_value || 0) / data.target_value) * 100))
                : 0;
              return (
                <div key={obj.id} className="border border-fuchsia-400/30 bg-fuchsia-400/5 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-bold text-white">{data.champion}</div>
                    {evalResult.met && (
                      <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-400">Atteint ✓</span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-400 mb-2">{data.label}</div>
                  <div className="h-1.5 bg-zinc-900 border border-zinc-800 overflow-hidden">
                    <div className="h-full bg-fuchsia-400 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-[10px] font-mono text-zinc-500">
                    <span>
                      Actuel : {evalResult.current_value !== null
                        ? (typeof evalResult.current_value === 'number'
                            ? evalResult.current_value.toFixed(1)
                            : evalResult.current_value)
                        : '—'}
                    </span>
                    <span>{evalResult.games_played}/{data.target_games} games</span>
                  </div>
                  <button
                    onClick={() => onAbandon?.(obj.id)}
                    className="mt-2 text-[9px] uppercase tracking-wider text-zinc-600 hover:text-rose-400 transition"
                  >
                    Abandonner
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Suggestions champion */}
        {championSuggestions.length > 0 && (
          <div className="space-y-2">
            {championSuggestions.slice(0, 3).map(({ champion, suggestions }) => (
              <div key={champion} className="border border-zinc-800 bg-zinc-900/40 p-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-zinc-300">{champion}</div>
                  <button
                    onClick={() => setChampObjModal(champion)}
                    className="text-[10px] uppercase tracking-wider font-bold text-fuchsia-400 hover:text-fuchsia-300 transition flex items-center gap-1"
                  >
                    <Plus size={11} /> Définir un objectif
                  </button>
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  💡 {suggestions[0].description}
                </div>
              </div>
            ))}
          </div>
        )}

        {championObjectives.length === 0 && championSuggestions.length === 0 && (
          <div className="border border-dashed border-zinc-800 p-6 text-center text-zinc-500 text-xs">
            Pas encore d'objectif champion. Joue plus de games sur tes mains pour débloquer des suggestions.
          </div>
        )}
      </div>

      {/* Active blocks */}
      <div>
        <div className="text-[10px] tracking-[0.4em] text-cyan-400 uppercase font-semibold mb-3 flex items-center gap-2">
          <Target size={12} /> BLOCS ACTIFS
        </div>
        {active.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-8 text-center text-zinc-500 text-sm">
            Aucun mini-bloc actif. Lance-en un pour structurer ta progression.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {active.map(({ block, progress }) => (
              <BlockCard key={block.id} block={block} progress={progress} onComplete={onComplete} onAbandon={onAbandon} onFail={onFail} onDelete={onDelete} />
            ))}
          </div>
        )}
      </div>

      {/* Completed */}
      {completed.length > 0 && (
        <div>
          <div className="text-[10px] tracking-[0.4em] text-emerald-400 uppercase font-semibold mb-3 flex items-center gap-2">
            <Trophy size={12} /> BLOCS COMPLÉTÉS
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {completed.map(block => {
              const progress = calculateBlockProgress(block, games);
              return <BlockCard key={block.id} block={block} progress={progress} onComplete={onComplete} onAbandon={onAbandon} onFail={onFail} onDelete={onDelete} />;
            })}
          </div>
        </div>
      )}

      {/* V6.3 : Failed blocks */}
      {failed.length > 0 && (
        <div>
          <div className="text-[10px] tracking-[0.4em] text-rose-400 uppercase font-semibold mb-3 flex items-center gap-2">
            <XCircle size={12} /> Blocs en Échec
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {failed.map(block => {
              const progress = calculateBlockProgress(block, games);
              return <BlockCard key={block.id} block={block} progress={progress} onComplete={onComplete} onAbandon={onAbandon} onFail={onFail} onDelete={onDelete} />;
            })}
          </div>
        </div>
      )}

      {/* Abandoned */}
      {abandoned.length > 0 && (
        <div>
          <div className="text-[10px] tracking-[0.4em] text-zinc-500 uppercase font-semibold mb-3">Blocs abandonnés</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 opacity-60">
            {abandoned.map(block => {
              const progress = calculateBlockProgress(block, games);
              return <BlockCard key={block.id} block={block} progress={progress} onComplete={onComplete} onAbandon={onAbandon} onFail={onFail} onDelete={onDelete} />;
            })}
          </div>
        </div>
      )}

      {showCreate && (
        <CreateBlockModal
          onClose={() => setShowCreate(false)}
          onSave={async (block) => { await onCreate(block); setShowCreate(false); }}
          champions={champions}
          version={version}
          currentGameCount={games.length}
        />
      )}

      {showSuggestions && (
        <SuggestionsModal
          suggestions={suggestions}
          onClose={() => setShowSuggestions(false)}
          onAccept={async (block) => { await onCreate(block); setShowSuggestions(false); }}
          currentGameCount={games.length}
        />
      )}

      {/* V7.7.2 : Modale objectif champion */}
      {champObjModal && (
        <ChampionObjectiveModal
          champion={champObjModal}
          games={games}
          onClose={() => setChampObjModal(null)}
          onCreate={async (record) => {
            await onCreate(record);
            setChampObjModal(null);
          }}
        />
      )}
    </div>
  );
}
